import { Button, InputLabel, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import IosSwitch from '../../common/IosSwitch.tsx';
import { z } from 'zod';
import { ALPHABET_REGEX, IRAN_MOBILE_REGEX, NATIONAL_ID_REGEX } from '../../../constants/regex.ts';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import RHFAutoComplete from '../../common/RHFAutoComplete.tsx';
import IResponse from '../../../interfaces/response.ts';
import IRole from '../../../interfaces/role.ts';
import axios from 'axios';
import Grid from '@mui/material/Unstable_Grid2/Grid2';
import { DatePicker, PickersLocaleText } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { IUser } from '../../../interfaces/user.ts';
import CheckPermission from '../../common/CheckPermission.tsx';
import { User } from '../../../enum/user.tsx';

const AddUser = () => {
	const { t } = useTranslation();

	const customPtBRLocaleText: Partial<PickersLocaleText<any>> = {
		okButtonLabel: t('confirm'),
		cancelButtonLabel: t('cancel'),
	};
	const { type, id } = useParams();
	const [imageSrc, setImageSrc] = useState<File | null>(null);
	const [isSwitchEnabled, setIsSwitchEnabled] = useState<boolean>(false);
	const [roles, setRoles] = useState<IRole[]>();
	const navigate = useNavigate();
	const [birthDate, setBirthDate] = useState<Date | null>(null);
	const [userRole, setUserRole] = useState<{ id: number | null; label: string | null }>({ id: null, label: null });

	const addNewUserSchema = z.object({
		first_name: z
			.string()
			.min(3, t('invalid_min_name_message'))
			.regex(ALPHABET_REGEX, { message: t('invalid_name_message') }),
		last_name: z
			.string()
			.min(3, t('invalid_min_last_name_message'))
			.regex(ALPHABET_REGEX, { message: t('invalid_last_name_message') }),
		email: z.string().email(t('invalid_email_message')),
		nation_code: z.string().regex(NATIONAL_ID_REGEX, {
			message: t('invalid_national_identification_number_message'),
		}),
		phone: z.string().regex(IRAN_MOBILE_REGEX, {
			message: t('invalid_mobile_message'),
		}),
		password: z.string().trim().min(8, t('invalid_password_message_8_karakter')).optional(),
		role: z
			.object({
				id: z.number(),
				label: z.string(),
			})
			.optional(),
	});
	const {
		register,
		handleSubmit,
		control,
		formState: { errors },
		reset,
		setValue,
	} = useForm<z.infer<typeof addNewUserSchema>>({
		resolver: zodResolver(addNewUserSchema),
	});

	const handleSendProfileImage = (id: number) => {
		if (!imageSrc) {
			console.error('No image selected');
			return;
		}

		const formData = new FormData();
		formData.append('image', imageSrc);

		axios({
			url: `/admin/users/uploadProfile/${id}`,
			method: 'POST',
			data: formData,
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		});
	};

	const handleSaveInfo = (data: any) => {
		let userDate = new Date(birthDate ? birthDate : '');
		axios({
			url: `/admin/${type === 'marketers' ? 'users' : type === 'admins' ? 'admins' : 'users'}/` + (id ?? ''),
			method: id ? 'PUT' : 'POST',
			data: {
				...data,
				birth_date: userDate?.toISOString().split('T')[0],
				status: isSwitchEnabled ? 1 : 0,
				roles_id: type !== 'user' ? [data.role?.id] : '',
				is_admin: type === 'user' ? 0 : 1,
			},
		}).then(res => {
			if (res.data.success) {
				let id = res.data.data;
				handleSendProfileImage(Number(id) ?? null);
				navigate(`/admin/${type === 'admins' || type === 'admin' ? 'admin' : 'user'}`);
			}
		});
	};

	const handleCancel = () => {
		reset();
		navigate(-1);
		setValue('role', undefined);
		setImageSrc(null);
		setIsSwitchEnabled(false);
	};

	useEffect(() => {
		if (type === 'admins' || type === 'marketers' || type === 'admin') {
			axios<IResponse<IRole[]>>({
				url: '/admin/roles',
				params: {
					limit: 100,
				},
			}).then(res => {
				if (res.data.success) {
					setRoles(res.data.data);
				}
			});
		}
		if (id) {
			axios<IResponse<IUser>>({
				url: `/admin/${type === 'admins' ? type : 'users'}/${id}`,
				loading: true,
			}).then(res => {
				if (res.data.success) {
					setValue('first_name', res.data.data.first_name);
					setValue('last_name', res.data.data.last_name);
					setValue('email', res.data.data.email);
					setValue('nation_code', res.data.data.nation_code);
					setValue('phone', res.data.data.phone);
					setValue('role', { id: res.data.data.roles[0]?.id, label: res.data.data.roles[0]?.title });
					setImageSrc(res.data.data.image);
					setIsSwitchEnabled(res.data.data.status === 1);
					if (res.data.data.birth_date) {
						setBirthDate(new Date(res.data.data.birth_date));
					}
					console.log({ id: res.data.data.roles[0]?.id, label: res.data.data.roles[0]?.title });
					setUserRole({ id: res.data.data.roles[0]?.id, label: res.data.data.roles[0]?.title });
				}
			});
		}
	}, []);
	console.log(userRole);

	const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files && e.target.files[0]) {
			setImageSrc(e.target.files[0]);
		}
	};

	const handleDeleteImage = () => {
		setImageSrc(null);
	};

	const handleChangeBrithDate = (value: Date | null) => {
		setBirthDate(value);
	};
	const userCreate = User.UserCreate;
	const adminCreate = User.AdminCreate;
	let imageUrl;
	if (imageSrc instanceof File) {
		imageUrl = URL.createObjectURL(imageSrc);
	} else if (typeof imageSrc === 'string') {
		imageUrl = imageSrc;
	}
	return (
		<CheckPermission permissions={type === 'admin' ? adminCreate : userCreate} isPage>
			<div className="flex min-w-full flex-col sm:min-w-[500px] md:min-w-[750px] lg:min-w-[1000px]">
				<div className="bg-white p-6">
					<Typography className="text-xl font-bold">{id ? t('edit_user', { type: t(type!) }) : t('create_new_user', { type: t(type!) })}</Typography>

					<div className="my-10 flex items-center gap-6">
						<img src={imageUrl} alt="Avatar" className="h-24 w-24 object-cover" />
						<div className="">
							<Button component="label" role={undefined} variant="contained" tabIndex={-1}>
								{t('upload_picture')}
								<input className="hidden" type="file" onChange={handleImageChange} />
							</Button>
							<Button className="ms-4 mt-2 bg-grey-200 text-grey-600 md:mt-0" onClick={handleDeleteImage} disabled={!imageSrc}>
								{t('delete_picture')}
							</Button>
							<Typography dir="rtl" className="mt-4 text-right text-text-disabled md:text-left">
								{t('maximum_allowed_value')}
							</Typography>
						</div>
					</div>

					<form className="mx-auto flex w-full flex-col gap-6 rounded-lg bg-white p-5" onSubmit={handleSubmit(handleSaveInfo)}>
						<Grid container spacing={3}>
							<Grid xs={12} md={6}>
								<InputLabel className="float-start flex px-2 pb-2">{t('name')}</InputLabel>
								<TextField variant="outlined" fullWidth size="small" {...register('first_name')} error={!!errors.first_name} helperText={errors.first_name?.message} inputProps={{ maxLength: 50 }} />
							</Grid>
							<Grid xs={12} md={6}>
								<InputLabel className="float-start flex px-2 pb-2">{t('last_name')}</InputLabel>
								<TextField variant="outlined" fullWidth size="small" {...register('last_name')} error={!!errors.last_name} helperText={errors.last_name?.message} inputProps={{ maxLength: 100 }} />
							</Grid>

							<Grid xs={12} md={6}>
								<InputLabel className="float-start flex px-2 pb-2">{t('email')}</InputLabel>
								<TextField variant="outlined" fullWidth size="small" type="email" {...register('email')} error={!!errors.email} helperText={errors.email?.message} />
							</Grid>
							<Grid xs={12} md={6}>
								<InputLabel className="float-start flex px-2 pb-2">{t('national_identification_number')}</InputLabel>
								<TextField variant="outlined" fullWidth size="small" {...register('nation_code')} error={!!errors.nation_code} helperText={errors.nation_code?.message} />
							</Grid>

							<Grid xs={12} md={6}>
								<InputLabel className="float-start flex px-2 pb-2">{t('phone_number')}</InputLabel>
								<TextField variant="outlined" fullWidth size="small" {...register('phone')} error={!!errors.phone} helperText={errors.phone?.message} inputProps={{ maxLength: 11 }} />
							</Grid>
							{type !== 'user' && type !== 'users' && (
								<Grid xs={12} md={6}>
									<InputLabel className="float-start flex px-2 pb-2">{t('role')}</InputLabel>
									<RHFAutoComplete name="role" getOptionLabel={option => (typeof option === 'string' ? option : option.label)} control={control} options={roles ?? []} loading={!roles} fullWidth />
								</Grid>
							)}

							<Grid xs={12} md={6}>
								<InputLabel className="float-start flex px-2 pb-2">{id ? t('new-password') : t('password')}</InputLabel>
								<TextField variant="outlined" fullWidth size="small" {...register('password')} error={!!errors.password} helperText={errors.password?.message ? t(errors.password.message) : ''} />
							</Grid>
							<Grid xs={12} md={6}>
								<InputLabel className="float-start flex px-2 pb-2">{t('date_of_birth')}</InputLabel>
								<LocalizationProvider localeText={customPtBRLocaleText}>
									<DatePicker className="w-full" value={birthDate} onChange={handleChangeBrithDate} />
								</LocalizationProvider>
							</Grid>
							<Grid xs={12} md={12}>
								<IosSwitch label={t('status')} checked={isSwitchEnabled} setValue={setIsSwitchEnabled} />
							</Grid>

							<Grid xs={12} className="flex flex-row gap-4">
								<Button variant="contained" className="my-2 w-full border-gray-400 text-white md:w-28" disabled={birthDate ? false : true} type="submit">
									{t('register')}
								</Button>
								<Button variant="outlined" className="my-2 w-full border-gray-400 text-text-secondary md:w-20" onClick={handleCancel}>
									{t('cancel')}
								</Button>
							</Grid>
						</Grid>
					</form>
				</div>
			</div>
		</CheckPermission>
	);
};

export default AddUser;
